import React, { useState } from "react";
import hosys from "@/api/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { resolveFileUrl } from "@/utils";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { EquipStatusBadge, MaintResultBadge } from "./TechnicalStatusBadge";
import { ChefHat, Plus, Pencil, Wrench, Upload, Trash2, FileText, ClipboardList } from "lucide-react";

const CATEGORIES = {
  cocció:         "Cocció",
  refrigeració:   "Refrigeració",
  preparació:     "Preparació",
  ventilació:     "Ventilació / Extracció",
  rentat:         "Rentat",
  mesura:         "Mesura / Control",
  seguretat:      "Seguretat",
  altres:         "Altres",
};

const MAINT_TYPES = {
  revisio_periodica:     "Revisió periòdica",
  neteja_tecnica:        "Neteja tècnica",
  reparacio:             "Reparació",
  avaria:                "Avaria",
  inspeccio:             "Inspecció",
  substitucio:           "Substitució",
  contracte_manteniment: "Contracte manteniment",
};

function DaysChip({ dateStr }) {
  if (!dateStr) return null;
  const diff = Math.ceil((new Date(dateStr) - new Date()) / 86400000);
  if (diff < 0)   return <span className="text-xs font-semibold text-red-600">({Math.abs(diff)}d)</span>;
  if (diff <= 30) return <span className="text-xs font-semibold text-orange-500">{diff}d</span>;
  if (diff <= 90) return <span className="text-xs font-semibold text-yellow-600">{diff}d</span>;
  return null;
}

function EquipForm({ item, venueId, onSave, onClose }) {
  const empty = {
    venue_id: venueId, name: "", category: "cocció",
    brand: "", model: "", serial_number: "", location: "",
    purchase_date: "", warranty_end_date: "",
    maintenance_company: "", maintenance_company_phone: "", maintenance_company_email: "",
    last_maintenance_date: "", next_maintenance_date: "",
    maintenance_frequency_months: 6, manual_url: "", certificate_url: "", photo_url: "",
    status: "operatiu", notes: "", active: true,
  };
  const [form, setForm] = useState(item || empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const uploadFile = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(field);
    const { file_url } = await hosys.integrations.Core.UploadFile({ file });
    set(field, file_url);
    setUploading(null);
  };

  const handleSave = async () => {
    setSaving(true);
    if (item?.id) {
      await hosys.entities.KitchenEquipment.update(item.id, form);
    } else {
      await hosys.entities.KitchenEquipment.create(form);
    }
    setSaving(false);
    onSave();
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{item ? "Editar equip" : "Nou equip de cuina"}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Nom de l'equip *</Label>
              <Input value={form.name} onChange={e => set("name", e.target.value)} placeholder="Ex: Forn convecció Rational" />
            </div>
            <div>
              <Label>Categoria *</Label>
              <Select value={form.category} onValueChange={v => set("category", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORIES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Estat</Label>
              <Select value={form.status} onValueChange={v => set("status", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="operatiu">Operatiu</SelectItem>
                  <SelectItem value="en_manteniment">En manteniment</SelectItem>
                  <SelectItem value="avariat">Avariat</SelectItem>
                  <SelectItem value="baixa">Baixa</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Marca</Label>
              <Input value={form.brand} onChange={e => set("brand", e.target.value)} />
            </div>
            <div>
              <Label>Model</Label>
              <Input value={form.model} onChange={e => set("model", e.target.value)} />
            </div>
            <div>
              <Label>Núm. de sèrie</Label>
              <Input value={form.serial_number} onChange={e => set("serial_number", e.target.value)} />
            </div>
            <div>
              <Label>Ubicació</Label>
              <Input value={form.location} onChange={e => set("location", e.target.value)} />
            </div>
            <div>
              <Label>Data de compra</Label>
              <Input type="date" value={form.purchase_date} onChange={e => set("purchase_date", e.target.value)} />
            </div>
            <div>
              <Label>Fi de garantia</Label>
              <Input type="date" value={form.warranty_end_date} onChange={e => set("warranty_end_date", e.target.value)} />
            </div>
            <div>
              <Label>Últim manteniment</Label>
              <Input type="date" value={form.last_maintenance_date} onChange={e => set("last_maintenance_date", e.target.value)} />
            </div>
            <div>
              <Label>Proper manteniment</Label>
              <Input type="date" value={form.next_maintenance_date} onChange={e => set("next_maintenance_date", e.target.value)} />
            </div>
            <div>
              <Label>Periodicitat (mesos)</Label>
              <Input type="number" value={form.maintenance_frequency_months} onChange={e => set("maintenance_frequency_months", Number(e.target.value))} />
            </div>
          </div>

          <div className="border-t pt-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Empresa manteniment</p>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Empresa</Label><Input value={form.maintenance_company} onChange={e => set("maintenance_company", e.target.value)} /></div>
              <div><Label>Telèfon</Label><Input value={form.maintenance_company_phone} onChange={e => set("maintenance_company_phone", e.target.value)} /></div>
              <div><Label>Email</Label><Input value={form.maintenance_company_email} onChange={e => set("maintenance_company_email", e.target.value)} /></div>
            </div>
          </div>

          <div className="border-t pt-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Documents i imatges</p>
            <div className="grid grid-cols-3 gap-3">
              {[["photo_url", "Foto equip"], ["manual_url", "Manual"], ["certificate_url", "Certificat"]].map(([field, label]) => (
                <div key={field}>
                  <Label>{label}</Label>
                  {form[field] ? (
                    <div className="flex items-center gap-1 mt-1">
                      <a href={form[field]} target="_blank" rel="noreferrer" className="text-xs text-blue-600 underline">Veure</a>
                      <button onClick={() => set(field, "")} className="text-gray-400 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  ) : (
                    <label className="mt-1 flex items-center gap-1.5 cursor-pointer text-xs text-gray-500 border border-dashed border-gray-300 rounded-lg px-2 py-1.5">
                      {uploading === field ? "Pujant..." : <><Upload className="w-3 h-3" /> Pujar</>}
                      <input type="file" className="hidden" accept=".pdf,.jpg,.png" onChange={e => uploadFile(e, field)} />
                    </label>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div><Label>Notes</Label><Textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={2} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel·lar</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#7B2D3F] hover:bg-[#6a2636] text-white">
            {saving ? "Guardant..." : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MaintenanceForm({ equipment, venueId, onSave, onClose }) {
  const [form, setForm] = useState({
    venue_id: venueId,
    asset_type: "kitchen_equipment",
    asset_id: equipment.id,
    asset_name: equipment.name,
    maintenance_type: "revisio_periodica",
    maintenance_date: new Date().toISOString().slice(0, 10),
    next_date: "", technician_name: "", company: equipment.maintenance_company || "",
    description: "", findings: "", actions_taken: "", corrective_actions: "",
    cost: "", document_url: "", result: "favorable", status: "completed", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const { file_url } = await hosys.integrations.Core.UploadFile({ file });
    set("document_url", file_url);
    setUploading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    await hosys.entities.TechnicalMaintenance.create({ ...form, cost: form.cost ? Number(form.cost) : undefined });
    // Update equipment last/next dates
    const updates = { last_maintenance_date: form.maintenance_date };
    if (form.next_date) updates.next_maintenance_date = form.next_date;
    await hosys.entities.KitchenEquipment.update(equipment.id, updates);
    setSaving(false);
    onSave();
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Registrar manteniment — {equipment.name}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tipus *</Label>
              <Select value={form.maintenance_type} onValueChange={v => set("maintenance_type", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(MAINT_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Resultat</Label>
              <Select value={form.result} onValueChange={v => set("result", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="favorable">Favorable</SelectItem>
                  <SelectItem value="favorable_condicions">Favorable amb condicions</SelectItem>
                  <SelectItem value="desfavorable">Desfavorable</SelectItem>
                  <SelectItem value="pendent">Pendent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Data intervenció *</Label>
              <Input type="date" value={form.maintenance_date} onChange={e => set("maintenance_date", e.target.value)} />
            </div>
            <div>
              <Label>Propera intervenció</Label>
              <Input type="date" value={form.next_date} onChange={e => set("next_date", e.target.value)} />
            </div>
            <div>
              <Label>Tècnic</Label>
              <Input value={form.technician_name} onChange={e => set("technician_name", e.target.value)} />
            </div>
            <div>
              <Label>Empresa</Label>
              <Input value={form.company} onChange={e => set("company", e.target.value)} />
            </div>
          </div>
          <div><Label>Descripció</Label><Textarea value={form.description} onChange={e => set("description", e.target.value)} rows={2} /></div>
          <div><Label>Resultats / Observacions</Label><Textarea value={form.findings} onChange={e => set("findings", e.target.value)} rows={2} /></div>
          <div><Label>Accions correctives</Label><Textarea value={form.corrective_actions} onChange={e => set("corrective_actions", e.target.value)} rows={2} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Cost (€)</Label><Input type="number" value={form.cost} onChange={e => set("cost", e.target.value)} /></div>
            <div>
              <Label>Document</Label>
              {form.document_url ? (
                <div className="flex items-center gap-1 mt-1">
                  <a href={form.document_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 underline">Veure doc.</a>
                  <button onClick={() => set("document_url", "")} className="text-gray-400 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                </div>
              ) : (
                <label className="mt-1 flex items-center gap-1.5 cursor-pointer text-xs text-gray-500 border border-dashed border-gray-300 rounded-lg px-2 py-1.5">
                  {uploading ? "Pujant..." : <><Upload className="w-3 h-3" /> Pujar</>}
                  <input type="file" className="hidden" accept=".pdf,.jpg,.png" onChange={uploadFile} />
                </label>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel·lar</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#7B2D3F] hover:bg-[#6a2636] text-white">
            {saving ? "Guardant..." : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function KitchenEquipmentTab({ items, maintenances, onRefresh, venueId }) {
  const [showEquipForm, setShowEquipForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showMaintForm, setShowMaintForm] = useState(false);
  const [maintEquip, setMaintEquip] = useState(null);
  const [filterCat, setFilterCat] = useState("all");

  const filtered = filterCat === "all" ? items : items.filter(e => e.category === filterCat);

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Select value={filterCat} onValueChange={setFilterCat}>
            <SelectTrigger className="w-48 h-8 text-xs">
              <SelectValue placeholder="Totes les categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Totes les categories</SelectItem>
              {Object.entries(CATEGORIES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-xs text-gray-400">{filtered.length} equips</span>
        </div>
        <Button onClick={() => { setEditing(null); setShowEquipForm(true); }} className="bg-[#7B2D3F] hover:bg-[#6a2636] text-white gap-1.5" size="sm">
          <Plus className="w-4 h-4" /> Nou equip
        </Button>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-14">
          <ChefHat className="w-12 h-12 text-gray-200 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">Cap equip registrat</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map(eq => {
            const myMaints = maintenances.filter(m => m.asset_id === eq.id);
            return (
              <div key={eq.id} className="bg-white border border-gray-100 rounded-xl p-3 hover:shadow-sm transition-shadow">
                {eq.photo_url && (
                  <img src={resolveFileUrl(eq.photo_url)} alt={eq.name} className="w-full h-24 object-cover rounded-lg mb-2" />
                )}
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-sm font-semibold text-gray-900">{eq.name}</span>
                      <EquipStatusBadge status={eq.status} />
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">{CATEGORIES[eq.category]}{eq.location && ` · ${eq.location}`}</p>
                    {(eq.brand || eq.model) && (
                      <p className="text-xs text-gray-500">{[eq.brand, eq.model].filter(Boolean).join(" · ")}</p>
                    )}
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => { setEditing(eq); setShowEquipForm(true); }} className="h-7 w-7 p-0 flex-shrink-0">
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <div className="text-xs text-gray-500 space-y-0.5 mb-2">
                  <div className="flex items-center gap-1">
                    <span className="text-gray-400">Proper mant.: </span>
                    <span>{eq.next_maintenance_date ? new Date(eq.next_maintenance_date).toLocaleDateString("ca-ES") : "—"}</span>
                    <DaysChip dateStr={eq.next_maintenance_date} />
                  </div>
                  {myMaints.length > 0 && (
                    <p className="text-gray-400">{myMaints.length} intervenció{myMaints.length > 1 ? "ns" : ""} registrada{myMaints.length > 1 ? "des" : ""}</p>
                  )}
                </div>
                <Button
                  size="sm" variant="outline"
                  onClick={() => { setMaintEquip(eq); setShowMaintForm(true); }}
                  className="w-full text-xs h-7 gap-1"
                >
                  <Wrench className="w-3 h-3" /> Registrar manteniment
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {showEquipForm && (
        <EquipForm
          item={editing}
          venueId={venueId}
          onSave={() => { setShowEquipForm(false); setEditing(null); onRefresh(); }}
          onClose={() => { setShowEquipForm(false); setEditing(null); }}
        />
      )}
      {showMaintForm && maintEquip && (
        <MaintenanceForm
          equipment={maintEquip}
          venueId={venueId}
          onSave={() => { setShowMaintForm(false); setMaintEquip(null); onRefresh(); }}
          onClose={() => { setShowMaintForm(false); setMaintEquip(null); }}
        />
      )}
    </div>
  );
}